import buildFiltersForPitchingSummary from '../filters/pitchingSummaryFilters.js';
import PerformanceComparisonBase from '../valueObjects/comparisonData/PerformanceComparisonBase.js';
import * as Enums from '../enums/index.js';
import { PlateDisciplineStatsCalculator, UniversalPlateDisciplineStatsCalculator } from  '../calculators/index.js';
import { ChartSeries, Series, BaseballResultsProcessor } from '../entities/index.js';

class PitchersPlateDisciplineSeriesProvider
{
    constructor(parameters, customResultsProcessor, plateDisciplineStatsCalculator) {
        this.resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();

        this.plateDisciplineStatsCalculator = plateDisciplineStatsCalculator || new UniversalPlateDisciplineStatsCalculator();

        if (!(this.plateDisciplineStatsCalculator instanceof PlateDisciplineStatsCalculator)) {
            throw new TypeError('plateDisciplineCalculator must be an instance inheriting from PlateDisciplineStatsCalculator class');
        }

        if (!(this.resultsProcessor instanceof BaseballResultsProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from BaseballResultsProcessor class');
        }

        if (typeof(parameters) === 'undefined') {
            parameters = [
                Enums.PitchingStats.PitchUsage.key,
                Enums.PitchingStats.KZoneRate.key,
                Enums.PitchingStats.StrikeoutRate.key,
                Enums.PitchingStats.SwingRate.key,
                Enums.PitchingStats.WhiffRate.key,
                Enums.PitchingStats.SwingingStrikeRate.key,
                Enums.PitchingStats.ZSwingRate.key,
                Enums.PitchingStats.ZContactRate.key,
                Enums.PitchingStats.OSwingRate.key,
                Enums.PitchingStats.OContactRate.key,
            ];
        }
        else if (!(parameters instanceof Array)) {
            throw new TypeError('parameters must be an array containing name of data parameters from Enums.PitchingStats');
        }

        this.parameters = parameters;
    }

    buildFiltersArray({
        // tagging context
        pitchTypes, pitcherIds, pitcherHandedness, pitchResults, batterIds, batterHandedness,
        // scoring context
        outs, balls, strikes, runnersOn, //runnersNotOn ?
    }) {
        return buildFiltersForPitchingSummary({
            pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
            outs, balls, strikes, runnersOn,
        });
    }

    getPlayerValue(stats, parameterName, statType) {
        const value = {
            [statType]: NaN
        };

        if (stats.hasOwnProperty(parameterName)) {
            value[statType] = stats[parameterName];
        }

        // for better compatibility
        value.percent = value[statType];

        return value;
    }

    getComparisonValue(comparisonValue, pitchType, parameterName, statType) {
        const value = {
            [statType]: NaN,
        };

        if (comparisonValue.hasStat(pitchType, parameterName, statType)) {
            value[statType] = comparisonValue.getStat(pitchType, parameterName, statType);
        }

        // for better compatibility
        value.percent = value[statType];

        return value;
    }

    getData(
        results,
        {
            // tagging context
            pitchType, pitcherIds, pitcherHandedness, pitchResults, batterIds, batterHandedness,
            // scoring context
            outs, balls, strikes, runnersOn,
        } = {},
        comparisonData
    ) {
        if (typeof(pitchType) === 'undefined') {
            throw new TypeError('pitchType must be defined');
        }

        if (!(results instanceof Array)) {
            throw new TypeError('results must be an array');
        }

        if (typeof(comparisonData) === 'undefined') {
            comparisonData = [];
        }
        else if (!(comparisonData instanceof Array)) {
            throw new TypeError('comparisonData must be an array containing instances inheriting from PerformanceComparisonBase class');
        }
        else if (comparisonData.some(c => !(c instanceof PerformanceComparisonBase))) {
            throw new TypeError('comparisonData can contain only instances inheriting from PerformanceComparisonBase class');
        }

        const filters = this.buildFiltersArray({
            pitchTypes: [pitchType], pitcherIds, pitcherHandedness, pitchResults, batterIds, batterHandedness,
            outs, balls, strikes, runnersOn,
        });
        const filteredResults = this.resultsProcessor.filterData(results, filters);

        const allPitchTypesFilters = this.buildFiltersArray({
            pitchTypes: [], pitcherIds, pitcherHandedness, pitchResults, batterIds, batterHandedness,
            outs, balls, strikes, runnersOn,
        });
        const allPitchTypesResults = this.resultsProcessor.filterData(results, allPitchTypesFilters);

        // get plate discipline stats
        const plateDisciplineTotal = this.plateDisciplineStatsCalculator.calculatePlateDiscipline(filteredResults);
        const plateDiscipline = this.plateDisciplineStatsCalculator.calculatePlateDisciplinePercentages(plateDisciplineTotal);

        const plateDisciplineTotalForAllPitchTypes = this.plateDisciplineStatsCalculator.calculatePlateDiscipline(allPitchTypesResults);

        // workaround to get pitch usage... todo - make it better
        if (plateDisciplineTotalForAllPitchTypes && plateDisciplineTotalForAllPitchTypes[Enums.PitchingStats.NumberOfPitches.key]) {
            plateDiscipline[Enums.PitchingStats.PitchUsage.key] = 100 * plateDisciplineTotal[Enums.PitchingStats.NumberOfPitches.key] / plateDisciplineTotalForAllPitchTypes[Enums.PitchingStats.NumberOfPitches.key];
        }

        const chartSeries = new ChartSeries(undefined, this.parameters);
        const dataSeries = [];

        // get series with data from results
        if (plateDiscipline) {
            for (let i = 0; i < this.parameters.length; i++) {
                dataSeries.push(
                    this.getPlayerValue(plateDiscipline, this.parameters[i], Enums.StatType.Avg.key)
                );
            }
        }

        chartSeries.push(new Series('Results', 1, dataSeries));

        // get data for comparison
        for (let c = 0; c < comparisonData.length; c++) {
            const compareTo = comparisonData[c];
            const comparisonSeries = [];

            for (let i = 0; i < this.parameters.length; i++) {
                comparisonSeries.push(
                    this.getComparisonValue(compareTo, pitchType, this.parameters[i], Enums.StatType.Avg.key)
                );
            }

            chartSeries.push(new Series(compareTo.name, 1, comparisonSeries));
        }

        return chartSeries;
    }
}

export default PitchersPlateDisciplineSeriesProvider;